// Naming + saving helpers for the list exports (PDF / Excel / ZIP).
//
// Every export leaves the app with the same name shape:
//
//     <CURRENCY>_<Prefix>_<dd-mm-yyyy>_<n>.<ext>   e.g. GBP_SmExp_20-09-2026_1.zip
//
// The server builds that name now, including <n>, which it counts per user,
// module, file type and day in the database. It therefore survives a cleared
// browser and is the same on every device. The local counter below is only a
// fallback for when the server does not tell us the name - see
// `resolveExportFilename`, which is the one function every download path should
// use to answer "what do I call this file?".

import { resolveApiAssetUrl } from "../config/url";

// One prefix per list, so the three exports stay apart in a download folder.
export const EXPORT_PREFIX = {
  expense: "SmExp",
  invoice: "SmInv",
  quotation: "SmQuo",
};

const SEQUENCE_KEY = "sb.exportSequences";

// dd-mm-yyyy, the format the download names use.
export function exportDateStamp(date = new Date()) {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}-${month}-${date.getFullYear()}`;
}

function readSequences() {
  try {
    const raw = localStorage.getItem(SEQUENCE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (err) {
    // Private mode or a corrupted entry - start counting from scratch.
    return {};
  }
}

// Returns the number to stamp on this download and remembers it for the next
// one. Yesterday's counters are dropped on the way through.
function nextSequence(bucket, stamp) {
  const key = `${bucket}|${stamp}`;
  const stored = readSequences();
  const kept = {};
  Object.keys(stored).forEach((k) => {
    if (k.endsWith(`|${stamp}`)) kept[k] = stored[k];
  });

  const next = (Number(kept[key]) || 0) + 1;
  kept[key] = next;
  try {
    localStorage.setItem(SEQUENCE_KEY, JSON.stringify(kept));
  } catch (err) {
    // Not being able to remember the counter only means the next download
    // reuses this number - not worth failing the download over.
  }
  return next;
}

// Builds the download name and advances that file kind's counter for today.
// `currencyCode` falls back to ALL when the list is not filtered to one currency.
//
// Calling this is what moves the local counter, so it must only run when the
// server has given us no name at all: otherwise the two counters drift apart
// and the fallback starts handing out numbers the server has already used.
export function buildExportName({
  currencyCode,
  extension,
  prefix = EXPORT_PREFIX.expense,
  date = new Date(),
}) {
  const code = String(currencyCode || "ALL").toUpperCase().replace(/[^A-Z0-9]/g, "") || "ALL";
  const ext = String(extension).replace(/^\./, "").toLowerCase();
  const stamp = exportDateStamp(date);
  const sequence = nextSequence(`${code}|${prefix}|${ext}`, stamp);
  return `${code}_${prefix}_${stamp}_${sequence}.${ext}`;
}

// A name is about to be handed to `link.download`, so drop any directory part
// and the quoting the header may have wrapped it in.
function sanitizeFilename(name) {
  if (!name || typeof name !== "string") return "";
  const base = name.trim().replace(/^["']|["']$/g, "").split(/[\\/]/).pop();
  // eslint-disable-next-line no-control-regex
  return base.replace(/[\u0000-\u001f]/g, "").trim();
}

// Pulls the name out of `Content-Disposition: attachment; filename="..."`. The
// header is only readable because the API sends
// `Access-Control-Expose-Headers: Content-Disposition`.
export function filenameFromContentDisposition(header) {
  if (!header || typeof header !== "string") return "";

  // RFC 5987's filename*= wins where both forms are present.
  const extended = header.match(/filename\*\s*=\s*[^']*'[^']*'([^;]+)/i);
  if (extended) {
    try {
      return sanitizeFilename(decodeURIComponent(extended[1].trim()));
    } catch (err) {
      // Badly encoded - fall through to the plain filename=.
    }
  }

  const plain =
    header.match(/filename\s*=\s*"([^"]+)"/i) || header.match(/filename\s*=\s*([^;]+)/i);
  return plain ? sanitizeFilename(plain[1]) : "";
}

// Reads one header out of either an axios headers object or a fetch Headers.
function headerValue(headers, name) {
  if (!headers) return "";
  if (typeof headers.get === "function") return headers.get(name) || "";
  const wanted = name.toLowerCase();
  const key = Object.keys(headers).find((k) => k.toLowerCase() === wanted);
  return key ? headers[key] : "";
}

// The single answer to "what do I call this file?", in the order the server's
// authority runs out:
//
//   1. `file_name` from a JSON response,
//   2. the filename in Content-Disposition on a streamed file,
//   3. the locally computed name - and only this step touches the counter.
export function resolveExportFilename({
  fileName,
  headers,
  currencyCode,
  prefix,
  extension,
  date,
} = {}) {
  const fromJson = sanitizeFilename(fileName);
  if (fromJson) return fromJson;

  const fromHeader = filenameFromContentDisposition(headerValue(headers, "content-disposition"));
  if (fromHeader) return fromHeader;

  return buildExportName({ currencyCode, prefix, extension, date });
}

// The JSON export endpoints disagree on the shape of `response`: the two Excel
// ones wrap the URL in an array, the three ZIP ones send it bare. Accepts the
// whole response body or just its `response` field.
export function unwrapExportUrl(payload) {
  const value =
    payload && typeof payload === "object" && !Array.isArray(payload) && "response" in payload
      ? payload.response
      : payload;
  const url = Array.isArray(value) ? value[0] : value;
  return typeof url === "string" && url.trim() ? url.trim() : "";
}

// Saves a blob under the given name.
export function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Safari needs the URL to outlive the click, so revoke on the next tick.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Marks an error whose message came from the server and is meant to be shown
// as-is, so call sites can tell it apart from a network or coding failure.
export function exportError(message) {
  const err = new Error(message);
  err.userMessage = message;
  return err;
}

// The message to toast for a failed export: the server's words when we have
// them, the caller's generic line otherwise.
export function exportErrorMessage(error, fallback) {
  return error?.userMessage || fallback;
}

// Export errors all look like { status: "0", message: ["..."] }.
function messageFromPayload(payload) {
  if (!payload) return null;
  if (typeof payload === "string") {
    const trimmed = payload.trim();
    return trimmed && !trimmed.startsWith("<") ? trimmed : null;
  }
  if (typeof payload !== "object") return null;

  const { message } = payload;
  if (Array.isArray(message)) return message.join(" ") || null;
  if (typeof message === "string") return message || null;
  if (typeof payload.error === "string") return payload.error || null;
  return null;
}

// Export failures are non-2xx now, so axios rejects and the body sits on
// `error.response.data` - as a Blob when the request asked for one.
export async function readRequestError(error) {
  const data = error?.response?.data;
  if (data == null) return null;

  let payload = data;
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    try {
      payload = JSON.parse((await data.text()).trim());
    } catch (err) {
      return null;
    }
  }
  return messageFromPayload(payload);
}

// Same, for a plain fetch Response - notably the 404 a swept export URL gives.
async function readResponseError(response) {
  let text = "";
  try {
    text = await response.text();
  } catch (err) {
    return null;
  }
  const trimmed = text.trim();
  if (!trimmed) return null;
  try {
    return messageFromPayload(JSON.parse(trimmed));
  } catch (err) {
    return null;
  }
}

// A failed export used to arrive as HTTP 200 with a JSON or HTML error body.
// Because the request asks for a blob, that body would otherwise be saved as a
// corrupt .pdf/.xlsx. The API no longer does this, but the sniff stays as
// defence in depth: it reads the blob back when it looks like text and returns
// the server's message, or null when the payload is a real file.
export async function readBlobError(blob) {
  if (!blob || blob.size === 0) return "The server returned an empty file.";
  const type = (blob.type || "").toLowerCase();
  const looksTextual =
    type.includes("json") || type.includes("html") || type.includes("text") || blob.size < 512;
  if (!looksTextual) return null;

  let text = "";
  try {
    text = await blob.text();
  } catch (err) {
    return null;
  }

  const trimmed = text.trim();
  if (!trimmed) return "The server returned an empty file.";
  if (trimmed.startsWith("%PDF") || trimmed.startsWith("PK")) return null;

  try {
    return messageFromPayload(JSON.parse(trimmed)) || trimmed.slice(0, 200);
  } catch (err) {
    // Not JSON - fall through and report the raw start of the body.
  }
  return trimmed.slice(0, 200);
}

function triggerDirectDownload(url, filename) {
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// Re-fetches one of the `/api/v3/exports/{user}/{nonce}/{file}` URLs the JSON
// endpoints hand back, and saves it under `filename`.
//
// Those URLs are GET and unauthenticated - the nonce in the path is the
// capability - so the request deliberately carries no Authorization header.
// They are pulled down as a blob rather than followed as a link because
// `download` is ignored cross-origin, which would leave the file under the
// server's own name for it.
//
// Generated exports are swept 24 hours after they are created; the 404 that
// leaves behind carries the "expired" message, which is raised rather than
// saved as a broken file.
export async function downloadExportFromUrl(url, filename) {
  const target = resolveApiAssetUrl(url);

  let response;
  try {
    response = await fetch(target);
  } catch (networkError) {
    // Blocked or offline: a plain link at least still gets the user the file.
    console.warn("Falling back to a direct export link:", networkError);
    triggerDirectDownload(target, filename);
    return;
  }

  if (!response.ok) {
    throw exportError(
      (await readResponseError(response)) ||
        `The export could not be downloaded (${response.status}).`
    );
  }

  const blob = await response.blob();
  const serverError = await readBlobError(blob);
  if (serverError) throw exportError(serverError);

  saveBlob(blob, filename);
}
